export type InventoryAdjustmentType = "adjustment" | "sku_transfer";
export type InventoryAdjustmentSource = "manual" | "csv";

/** One box-level line sent to POST /v1/swageazy-inventory/adjustments */
export interface InventoryAdjustmentLineRequest {
  product_id: number;
  sku: string;
  warehouse_id: string;
  box_id: string;
  /** Signed: positive adds stock, negative removes it. */
  quantity: number;
  expiry_date?: string;
  note?: string;
}

export interface InventoryAdjustmentRequest {
  reason: string;
  lines: InventoryAdjustmentLineRequest[];
}

export interface SkuTransferRequest {
  warehouse_id: string;
  from: { product_id: number; sku: string };
  to: { product_id: number; sku: string };
  boxes: { box_id: string; quantity: number }[];
  expiry_date?: string;
  reason: string;
}

export interface InventoryAdjustmentResultLine {
  row: number;
  product_id: number;
  sku: string;
  warehouse_id: string;
  box_id: string;
  quantity: number;
  quantity_before: number;
  quantity_after: number;
  log_id: number;
}

export interface InventoryAdjustmentResult {
  adjustment_id: number;
  adjustment_type: InventoryAdjustmentType;
  source: InventoryAdjustmentSource;
  line_count: number;
  total_added: number;
  total_removed: number;
  sheet_url: string | null;
  lines: InventoryAdjustmentResultLine[];
}

export interface InventoryAuditLogFilters {
  product_id?: number;
  admin_id?: number;
  sku?: string;
  type?: InventoryAdjustmentType | "";
  page_no: number;
  limit: number;
}

export interface InventoryAuditLogRow {
  id: number;
  adjustment_id: number;
  adjustment_type: InventoryAdjustmentType;
  source: InventoryAdjustmentSource;
  reason: string;
  note: string | null;
  product_id: number;
  product_name: string | null;
  sku: string;
  warehouse_id: string;
  box_id: string;
  /** Signed quantity. */
  quantity: number;
  quantity_before: number;
  quantity_after: number;
  from_product_id: number | null;
  from_sku: string | null;
  to_product_id: number | null;
  to_sku: string | null;
  admin_id: number | null;
  admin_name: string | null;
  platform: string | null;
  sheet_url: string | null;
  created_date: string;
}

export interface InventoryAuditLogResponse {
  page_no: number;
  total_pages: number;
  total_count: number;
  limit: number;
  list: InventoryAuditLogRow[];
}

/** `row` is 1-based: CSV data row, `lines` index + 1, or `boxes` index + 1. */
export interface InventoryAdjustmentRowError {
  row: number;
  message: string;
}

/** Normalised API failure for adjustment / transfer / CSV endpoints. */
export interface InventoryAdjustmentApiError {
  message: string;
  code?: string;
  rowErrors: InventoryAdjustmentRowError[];
}

/** Editable box-level row in the Adjust / Transfer popups. */
export interface StockAdjustmentRow {
  key: string;
  sku: string;
  city: string;
  warehouse_id: string;
  box_id: string;
  rack: string;
  /** SKU stock in this box. */
  on_hand: number;
  existing_expiry_date?: string;
  selected: boolean;
  /** Raw input so a lone "-" can be typed. */
  quantity: string;
  expiry_date?: string;
  note: string;
}

/** SKU × warehouse totals shown above the box rows. */
export interface SkuWarehouseSummary {
  key: string;
  sku: string;
  city: string;
  warehouse_id: string;
  on_hand: number;
  blocked: number;
  available: number;
}

export const INVENTORY_ADJUSTMENT_TYPE_OPTIONS: {
  id: InventoryAdjustmentType | "";
  value: string;
}[] = [
  { id: "", value: "All types" },
  { id: "adjustment", value: "Adjustment" },
  { id: "sku_transfer", value: "SKU Transfer" },
];

export const INVENTORY_ADJUSTMENT_CSV_HEADERS = [
  "product_id",
  "sku",
  "warehouse_id",
  "box_id",
  "quantity",
  "expiry_date",
  "note",
];

export const INVENTORY_ADJUSTMENT_REASON_MIN = 3;
export const INVENTORY_ADJUSTMENT_REASON_MAX = 512;
export const INVENTORY_ADJUSTMENT_NOTE_MAX = 512;
export const INVENTORY_ADJUSTMENT_MAX_LINES = 500;
export const SKU_TRANSFER_MAX_BOXES = 50;
export const INVENTORY_BOX_ID_PATTERN = /^B\d{5}$/i;

// ---- Product transactions (GET /products/:product_id/transactions) ----

export type InventoryLedgerLogType =
  | "credit"
  | "debit"
  | "block"
  | "return"
  | "release"
  | "transfer_in"
  | "transfer_out"
  | "adjust_in"
  | "adjust_out"
  | "sku_move_in"
  | "sku_move_out";

/** Page size is fixed server-side; `limit` is ignored. */
export const PRODUCT_TRANSACTIONS_PAGE_SIZE = 100;

/** Ledger types that add to / remove from on-hand stock. Block and release only move blocked stock. */
export const LEDGER_IN_TYPES: InventoryLedgerLogType[] = [
  "credit",
  "return",
  "transfer_in",
  "adjust_in",
  "sku_move_in",
];
export const LEDGER_OUT_TYPES: InventoryLedgerLogType[] = [
  "debit",
  "transfer_out",
  "adjust_out",
  "sku_move_out",
];

export interface ProductTransactionFilters {
  product_id: number;
  sku?: string;
  page_no: number;
}

export interface ProductTransactionRow {
  log_id: number;
  log_type: InventoryLedgerLogType;
  activity_type: string;
  product_id: number;
  product_name: string | null;
  sku: string;
  /** Unsigned — use `log_type` for direction. */
  quantity: number;
  quantity_rejected: number;
  rejection_reason: string | null;
  order_id: number | null;
  pr_id: number | null;
  pr_raised_by: string | null;
  po_id: number | null;
  po_raised_by: string | null;
  warehouse_id: string;
  box_id: string;
  /** Box's current rack, not the rack at log time. */
  rack: string | null;
  from_box_id: string | null;
  to_box_id: string | null;
  expiry_date: string | null;
  admin_id: number | null;
  logged_by: string | null;
  log_date: string;
}

export interface ProductTransactionResponse {
  page_no: number;
  total_pages: number;
  total_count: number;
  limit: number;
  list: ProductTransactionRow[];
}

/** A product SKU added into a box during Adjust (positive quantity only). */
export interface NewAdjustmentSkuLine {
  /** Client-only id; row errors are keyed as `new:<id>`. */
  id: string;
  sku: string;
  warehouse_id: string;
  /** Only needed to generate a box. */
  rack: string;
  box_id: string;
  quantity: string;
  expiry_date?: string;
  note: string;
  isGeneratingBox: boolean;
}
